/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import customTable from '../../../../../share/components/vue/PaginationTable';
import customProgress from "../../../../../share/components/vue/CustomProgress";
import config from '../../../../../share/config';
import inputValidator from '../../../../../share/util/inputValidator';
import { apiErrorDefaultAction } from "../../../../../share/util/api/errorAction";
import { PHASE, SPWS_KEY_CODE } from './constants';
import { standby } from './standby';
import { readSelectedItems, downloadFile } from './read';
import { terminate } from './terminate';
import { checkKeys } from './checkKeys';
import { verifyPinNumber } from './verifyPinNumber';
import { readPreviewImages } from './readPreviewImages';
import { handleSort, getSortIconHtml, sortArray, setHighlight } from './tableHelpers';

export default {
  name: 'requirementSpES008',
  data() {
    return {
      systemId: "",
      requirementId: "ES8",
      modal_special_requirement_ES8: false,
      phase: PHASE.START,
      isLoading: false,
      confirmChecked: false,
      agreementChecked: false,
      modeList: [],
      selectedMode: "",
      historyList: [],
      // 履歴情報表示画面のヘッダー
      historyListColumns: [
        {
          name: 'selected',
          label: `${this.i18nWsKey}.header_preservation_target`,
          useSort: false
        },
        {
          name: "displayName",
          label: `${this.i18nWsKey}.header_history`,
          useSort: true
        }
      ],
      // 履歴情報表示画面のソート状態
      historyListCurrentSort: '',
      historyListSortDir: '',
      // プレビュー選択画面のソート状態
      previewSelectCurrentSort: '',
      previewSelectSortDir: '',
      // プレビュー情報表示画面のソート状態
      previewInfoCurrentSort: '',
      previewInfoSortDir: '',
      totalDidCount: 0,
      processedDidCount: 0,
      progress: 0,
      progressMax: 100,
      downloadUuid: null,
      // 押されたキーコードの配列
      keyCode: [],
      isCompressed: false,
      fileUrl: undefined,
      fileExtension: '',
      inputData: '', // パスワード入力値
      isAlert: { data: { valid: null, msg: '' } }, // アラート情報
      // プレビュー選択画面の表示項目
      previewSelectItems: [],
      // プレビュー選択画面のヘッダー
      previewSelectColumns: [
        {
          name: "displayName",
          label: `${this.i18nWsKey}.header_history`,
          useSort: true
        }
      ],
      // プレビュー選択画面で選択された項目
      selectedPreviewItem: null,
      // プレビュー情報表示画面の表示項目
      previewInfoItems: [],
      // プレビュー情報表示画面のヘッダー
      previewInfoColumns: [
        {
          name: "date_time",
          label: `individual.header_vehicle_dtc_table_date_time_occurrence`,
          useSort: true
        },
        {
          name: "ig_on_time",
          label: `individual.label_table_header_ig_on_count`,
          useSort: true
        }
      ],
      // プレビュー情報表示画面で選択された項目
      selectedDateItem: null,
      imageData: {}
    };
  },

  components: {
    'custom-Progress': customProgress,
    "custom-table": customTable
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.FAILED:
        case PHASE.COMMON_ERROR:
        case PHASE.SAVE_FAILED:
        case PHASE.PASSWORD_MISMATCH:
        case PHASE.PREVIEW_FAILED:
          return 'danger';
        case PHASE.START:
          return 'warning';
        case PHASE.SELECT:
        case PHASE.HISTORY_LIST:
        case PHASE.READ:
        case PHASE.PARTIAL_FAILED:
        case PHASE.INPUT_PASSWORD:
        case PHASE.CONFIRM_CONSENT:
        case PHASE.PREVIEW_SELECT:
        case PHASE.PREVIEW_INFO:
        case PHASE.PREVIEW_DISPLAY:
        default:
          return 'secondary';
      }
    },

    /**
     * 表示ヘッダー文字列判定
     * @returns {string} ヘッダー文字列
     */
    headerTitle() {
      const confirmTitle = 'message.common_title_confirmation';
      const executeTitle = 'individual.common_title_execution';
      const warningTitle = 'message.common_title_warning';
      const successTitle = 'message.common_title_success';
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.START]: warningTitle,
        [PHASE.SELECT]: confirmTitle,
        [PHASE.HISTORY_LIST]: confirmTitle,
        [PHASE.READ]: executeTitle,
        [PHASE.SUCCESS]: successTitle,
        [PHASE.FAILED]: errorTitle,
        [PHASE.COMMON_ERROR]: errorTitle,
        [PHASE.PARTIAL_FAILED]: confirmTitle,
        [PHASE.SAVE_FAILED]: errorTitle,
        [PHASE.INPUT_PASSWORD]: confirmTitle,
        [PHASE.CONFIRM_CONSENT]: confirmTitle,
        [PHASE.PASSWORD_MISMATCH]: errorTitle,
        [PHASE.PREVIEW_SELECT]: confirmTitle,
        [PHASE.PREVIEW_INFO]: confirmTitle,
        [PHASE.PREVIEW_DISPLAY]: confirmTitle,
        [PHASE.PREVIEW_FAILED]: errorTitle,
      };

      return this.$t(titleMap[this.phase]);
    },

    /**
     * 作業サポート表示メッセージ判定
     * @returns {string} メッセージ
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.START]: `${this.i18nWsKey}.label_start`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_SP_WS_ES8_TXT_3`,
        [PHASE.FAILED]: `${this.i18nWsKey}.REQ_SP_WS_ES8_TXT_4`,
        [PHASE.COMMON_ERROR]: `${this.i18nWsKey}.REQ_SP_WS_ES8_TXT_5`,
        [PHASE.PARTIAL_FAILED]: `${this.i18nWsKey}.label_decryption_failure`,
        [PHASE.SAVE_FAILED]: `${this.i18nWsKey}.REQ_SP_WS_ES8_TXT_10`,
        [PHASE.INPUT_PASSWORD]: `${this.i18nWsKey}.label_password_request`,
        [PHASE.CONFIRM_CONSENT]: `${this.i18nWsKey}.REQ_SP_WS_ES8_TXT_7`,
        [PHASE.PASSWORD_MISMATCH]: `${this.i18nWsKey}.REQ_SP_WS_ES8_TXT_8`,
        [PHASE.PREVIEW_FAILED]: `${this.i18nWsKey}.REQ_SP_WS_ES8_TXT_11`,
      };
      return this.$t(messageMap[this.phase]);
    },

    /**
     * [戻る]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.START]: true,
        [PHASE.SELECT]: true,
        [PHASE.HISTORY_LIST]: true,
        [PHASE.READ]: false,
        [PHASE.SUCCESS]: false,
        [PHASE.FAILED]: false,
        [PHASE.COMMON_ERROR]: false,
        [PHASE.PARTIAL_FAILED]: false,
        [PHASE.SAVE_FAILED]: false,
        [PHASE.INPUT_PASSWORD]: true,
        [PHASE.CONFIRM_CONSENT]: true,
        [PHASE.PASSWORD_MISMATCH]: false,
        [PHASE.PREVIEW_SELECT]: true,
        [PHASE.PREVIEW_INFO]: true,
        [PHASE.PREVIEW_DISPLAY]: true,
        [PHASE.PREVIEW_FAILED]: false,
      };
      return showCancelButtonMap[this.phase];
    },

    /**
     * [OK]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.START]: false,
        [PHASE.SELECT]: false,
        [PHASE.HISTORY_LIST]: false,
        [PHASE.READ]: false,
        [PHASE.SUCCESS]: true,
        [PHASE.FAILED]: true,
        [PHASE.COMMON_ERROR]: true,
        [PHASE.PARTIAL_FAILED]: true,
        [PHASE.SAVE_FAILED]: true,
        [PHASE.INPUT_PASSWORD]: true,
        [PHASE.CONFIRM_CONSENT]: true,
        [PHASE.PASSWORD_MISMATCH]: true,
        [PHASE.PREVIEW_SELECT]: true,
        [PHASE.PREVIEW_INFO]: true,
        [PHASE.PREVIEW_DISPLAY]: false,
        [PHASE.PREVIEW_FAILED]: true,
      };
      return showOkButtonMap[this.phase];
    },

    /**
     * [次へ][選択][保存][OK][終了]ボタンの活性・非活性を制御
     * @returns {boolean} trueなら非活性
     */

    isDisableOk() {
      if (this.isLoading) return true;

      switch (this.phase) {
        case PHASE.START:
          return this.isDisableAgreementConfirm();
        case PHASE.SELECT:
          // 機能が選択されていない場合、非活性
          return !this.selectedMode;
        case PHASE.HISTORY_LIST:
          return this.selectedHistoryListItems.length === 0;
        case PHASE.INPUT_PASSWORD:
          // パスワードが入力されていない場合、非活性
          return this.inputData.length === 0;
        case PHASE.PREVIEW_SELECT:
          return !this.selectedPreviewItem;
        case PHASE.PREVIEW_INFO:
          return !this.selectedDateItem;

        // ここから下は全てisLoadingがfalseなら活性
        case PHASE.SUCCESS:
        case PHASE.FAILED:
        case PHASE.COMMON_ERROR:
        case PHASE.PARTIAL_FAILED:
        case PHASE.SAVE_FAILED:
        case PHASE.CONFIRM_CONSENT:
        case PHASE.PASSWORD_MISMATCH:
        case PHASE.PREVIEW_DISPLAY:
        case PHASE.PREVIEW_FAILED:
        default:
          return false;
      }
    },

    /**
     * 履歴情報表示用ソート済み配列
     * @returns {Array} ソート済み配列
     */
    sortedDisplayList() {
      const excludeSortVal = this.$t(`${this.i18nWsKey}.history_name_all`);
      return sortArray(this.historyList, this.historyListCurrentSort, this.historyListSortDir, excludeSortVal);
    },

    /**
     * プレビュー選択表示用ソート済み配列
     * @returns {Array} ソート済み配列
     */
    sortedPreviewList() {
      return sortArray(this.previewList, this.previewSelectCurrentSort, this.previewSelectSortDir);
    },

    /**
     * プレビュー情報表示用ソート済み配列
     * @returns {Array} ソート済み配列
     */
    sortedPreviewInfoItems() {
      return sortArray(this.previewInfoItems, this.previewInfoCurrentSort, this.previewInfoSortDir);
    },

    /**
     * 選択された項目
     * @returns {Array} ソート済み配列
     */
    selectedHistoryListItems() {
      return this.historyList.filter(item => item.selected === true && !item.isAllSelect);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modeList = [
        { value: "save", text: this.$t(`${this.i18nWsKey}.choices_save`) }
      ];
      this.modal_special_requirement_ES8 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_ES8 = false;
    },

    /** 
     * 通信エラー表示画面の表示
     */
    async showUnexpectedError() {
      await terminate(this, PHASE.COMMON_ERROR);
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    async validateErrorResult(result) {
      await apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * [戻る]ボタンクリック処理
     */
    async onCancelClicked() {
      switch (this.phase) {
        case PHASE.START:
          this.closeWizard();
          break;
        case PHASE.SELECT:
          document.removeEventListener('keydown', this.onKeyDown);
          document.removeEventListener('keyup', this.onKeyUp);
          await terminate(this);
          this.closeWizard();
          break;
        case PHASE.HISTORY_LIST:
        case PHASE.INPUT_PASSWORD:
        case PHASE.CONFIRM_CONSENT:
        case PHASE.PREVIEW_SELECT:
          this.toSelectPhase();
          break;
        case PHASE.PREVIEW_INFO:
          await checkKeys(this, PHASE.PREVIEW_SELECT, PHASE.PREVIEW_FAILED);
          break;
        case PHASE.PREVIEW_DISPLAY:
          this.toPreviewInfoPhase();
          break;
        default:
          // noop
          break;
      }
    },

    /**
     * [次へ][選択][保存][OK][終了]ボタンクリック処理
     */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.START:
          await standby(this);
          break;
        case PHASE.SELECT:
          switch (this.selectedMode) {
            case 'save':
              await checkKeys(this, PHASE.HISTORY_LIST, PHASE.SAVE_FAILED);
              break;
            case 'preview':
              await checkKeys(this, PHASE.PREVIEW_SELECT, PHASE.PREVIEW_FAILED);
              break;
          }
          document.removeEventListener('keydown', this.onKeyDown);
          document.removeEventListener('keyup', this.onKeyUp);
          break;
        case PHASE.HISTORY_LIST:
          await readSelectedItems(this);
          break;
        case PHASE.PARTIAL_FAILED:
          await downloadFile(this);
          break;
        case PHASE.INPUT_PASSWORD:
          // バリデーションチェック
          if (!this.validate()) {
            // エラーの場合、以降の処理を行わない
            return;
          }
          // パスワード一致判定
          await verifyPinNumber(this);
          // アラート情報をクリア
          this.isAlert = {
            data: { valid: null, msg: '' }
          };
          break;
        case PHASE.CONFIRM_CONSENT:
          this.modeList.push({ value: "preview", text: this.$t(`${this.i18nWsKey}.choices_preview`) });
          this.toSelectPhase();
          break;
        case PHASE.PASSWORD_MISMATCH:
          this.phase = PHASE.INPUT_PASSWORD;
          break;
        case PHASE.PREVIEW_SELECT:
          this.toPreviewInfoPhase();
          break;
        case PHASE.PREVIEW_INFO:
          await readPreviewImages(this);
          break;
        case PHASE.PREVIEW_DISPLAY:
          await terminate(this);
          this.closeWizard();
          break;
        case PHASE.SUCCESS:
        case PHASE.FAILED:
        case PHASE.COMMON_ERROR:
        case PHASE.SAVE_FAILED:
        case PHASE.PREVIEW_FAILED:
          this.closeWizard();
          break;
        default:
          // noop
          break;
      }
    },

    /**
     * チェックボックスが2つともチェックされるとfalse(PHASE.START)
     * @returns {boolean} trueなら非活性
     */
    isDisableAgreementConfirm() {
      return !this.confirmChecked || !this.agreementChecked;
    },

    /**
     * 機能選択画面への遷移、コマンド入力受けつけ
     */
    toSelectPhase() {
      this.selectedMode = "";
      this.phase = PHASE.SELECT;
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('keyup', this.onKeyUp);
    },

    /**
     * 表示項目の作成、プレビュー情報表示画面への遷移
     */
    toPreviewInfoPhase() {
      this.previewInfoCurrentSort = '';
      this.previewInfoSortDir = '';
      this.previewInfoItems = [];
      this.selectedDateItem = null;
      this.phase = PHASE.PREVIEW_INFO;
      this.$nextTick(() => {
        this.selectedPreviewItem.histories.forEach(item => {
          this.previewInfoItems.push({
            image_order: item.image_order,
            date_time: item.date_time,
            ig_on_time: item.ig_on_time
          });
        });
      });
    },

    /**
     * キーが離されたら呼ばれる
     * @param {Object} event イベント
     */
    onKeyUp(event) {
      const i = this.keyCode.indexOf(event.keyCode);
      // 配列にキーコードがあればその要素を削除
      if (i !== -1) { this.keyCode.splice(i, 1); }
      if (this.keyCode.length === 3) { this.inputKey(); }
    },

    /**
     * キーが押されたら呼ばれる
     * @param {Object} event イベント
     */
    onKeyDown(event) {
      // 配列に値がない、または処理中でなければ
      if (-1 === this.keyCode.indexOf(event.keyCode) && this.phase === PHASE.SELECT && !this.isLoading) {
        this.keyCode.push(event.keyCode); // キーコードを追加
        if (this.keyCode.length === 3) { this.inputKey(); }
      }
    },

    /** 特殊キーチェック */
    inputKey() {
      const keyPushed = SPWS_KEY_CODE.find(obj => JSON.stringify(obj.code) === JSON.stringify(this.keyCode.sort((a, b) => { return a - b; })));
      if (typeof keyPushed !== 'undefined' && this.modeList.length === 1) {
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('keyup', this.onKeyUp);
        this.phase = PHASE.INPUT_PASSWORD;
      }
    },

    /** バリデーションチェック */
    validate() {
      this.isAlert.data = inputValidator.validateCommons(
        this.inputData, true, Number.MAX_SAFE_INTEGER, /^[0-9]*$/
      );
      return this.isAlert.data.valid === null;
    },

    /**
     * ---
     * 履歴情報表示画面、プレビュー選択画面、プレビュー情報表示画面の表の操作
     * ---
     */

    /**
     * テーブル行に適用するスタイルを指定
     * @param {number} index インデックス
     * @returns 行の背景色
     */
    getTdStyle(index) {
      return { 'table-row-stripe': (index % 2 === 0) && true };
    },

    /** 
     * 履歴情報表示画面の項目選択時イベント
     * @param {object} selectedItem 選択された項目
     */
    historyListRowClickedEvent(selectedItem) {
      // itemが「すべて選択」の場合のみ処理を実行
      if (selectedItem.isAllSelect) {
        if (selectedItem.selected) {
          // 「すべて選択」を除くすべての項目のselectedがtrueの場合、すべての項目のselectedをfalseにする
          const otherItem = this.historyList.filter(i => !i.isAllSelect);
          const isAllSelected = otherItem.every(i => i.selected);
          if (isAllSelected) {
            this.historyList.forEach(i => {
              i.selected = false;
            });
          }
        } else {
          // 「すべて選択」のselectedがtrueの場合、すべての項目のselectedをtrueにする
          this.historyList.forEach(i => {
            i.selected = true;
          });
        }
      } else {
        selectedItem.selected = !selectedItem.selected;
      }
    },

    /**
     * historyListCurrentSort, sortDirに従ってソートする
     * @param {object} column カラムオブジェクト
     */
    historyListSort(column) {
      handleSort(this, column, 'historyListCurrentSort', 'historyListSortDir');
    },

    /**
     * 履歴情報表示画面のソートアイコンの作成
     * @returns {string} ソートアイコン
     */
    geHistoryListSortIcon() {
      if (this.historyListSortDir === config.ASC) {
        return "<span> ▲</span>";
      } else if (this.historyListSortDir === config.DESC) {
        return "<span> ▼</span>";
      } else {
        return "";
      }
    },

    /**
     * プレビュー選択画面の項目選択時イベント
     */
    previewSelectRowClickedEvent(selected) {
      this.selectedPreviewItem = selected;
      this.previewSelectSetHighLight();
    },

    /**
     * プレビュー選択画面の選択している機能のハイライト設定
     */
    previewSelectSetHighLight() {
      setHighlight(this.previewList, 'displayName', this.selectedPreviewItem.displayName, ['displayName']);
    },

    /**
     * previewSelectCurrentSort, previewSelectSortDirに従ってソートする
     * @param {object} column カラムオブジェクト
     */
    previewSelectSort(column) {
      handleSort(this, column, 'previewSelectCurrentSort', 'previewSelectSortDir');
    },

    /**
     * プレビュー選択画面のソートアイコンの作成
     * @returns {string} ソートアイコン
     */
    getPreviewSelectSortIcon() {
      return getSortIconHtml(this.previewSelectSortDir);
    },

    /**
     * プレビュー情報表示画面の項目選択時イベント
     */
    previewInfoRowClickedEvent(selected) {
      this.selectedDateItem = selected;
      this.previewInfoSetHighLight();
    },

    /**
     * プレビュー情報表示画面の選択している機能のハイライト設定
     */
    previewInfoSetHighLight() {
      setHighlight(this.previewInfoItems, 'image_order', this.selectedDateItem.image_order, ['date_time', 'ig_on_time']);
    },

    /**
     * previewInfoCurrentSort, previewInfoSortDirに従ってソートする
     * @param {object} column カラムオブジェクト
     */
    previewInfoSort(column) {
      handleSort(this, column, 'previewInfoCurrentSort', 'previewInfoSortDir');
    },

    /**
     * プレビュー情報表示画面のソートアイコンの作成
     * @returns {string} ソートアイコン
     */
    getPreviewInfoSortIcon() {
      return getSortIconHtml(this.previewInfoSortDir);
    },
  },

  mounted() {
    this.init();
  },
};